import { useEffect, useRef } from 'react';

export function CelestialSigil({ className = '' }: { className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Simple exponential smoothing — currentT catches up to scroll target
    // over several frames with no overshoot, no bounce, just a soft glide.
    let currentT = 0;
    let targetT = 0;
    let raf = 0;
    let lastY = -1;

    // Fraction of remaining distance covered per frame. Higher = snappier.
    const SMOOTHING = 0.18;

    const computeTarget = () => {
      const y = window.scrollY;
      if (y === lastY) return;
      lastY = y;
      targetT = Math.max(0, Math.min(1, y / window.innerHeight));
    };

    const render = () => {
      const delta = targetT - currentT;
      const settled = Math.abs(delta) < 0.0005;
      currentT = settled ? targetT : currentT + delta * SMOOTHING;

      const t = currentT;
      const eased = 1 - Math.pow(1 - t, 2);
      const vmin = Math.min(window.innerWidth, window.innerHeight);
      const vmaxVsVmin = Math.max(window.innerWidth, window.innerHeight) / vmin;
      const maxScale = Math.max(1.6, vmaxVsVmin * 1.15);
      const scale = 1 + eased * (maxScale - 1);
      const bump = Math.sin(Math.PI * t) * 0.3;
      const opacity = Math.max(0.32, 0.5 - eased * 0.18 + bump);
      const goldGlow = 16 * (1 - eased);
      const arcaneGlow = 30 + Math.sin(Math.PI * t) * 40;
      const arcaneAlpha = 0.5 * eased + Math.sin(Math.PI * t) * 0.3;
      const hue = eased * 120;
      el.style.opacity = String(opacity);
      el.style.transform = `translate(-50%, -50%) scale(${scale})`;
      el.style.filter =
        `drop-shadow(0 0 ${goldGlow}px rgba(201,165,92,${0.3 * (1 - eased)}))` +
        ` drop-shadow(0 0 ${arcaneGlow}px rgba(123,215,255,${arcaneAlpha}))` +
        ` hue-rotate(${hue}deg)`;

      // Keep looping while we haven't caught up to the scroll target.
      if (!settled) {
        raf = requestAnimationFrame(render);
      } else {
        raf = 0;
      }
    };

    const onScroll = () => {
      computeTarget();
      if (!raf) raf = requestAnimationFrame(render);
    };

    computeTarget();
    currentT = targetT; // no initial tween from 0
    render();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`pointer-events-none fixed left-1/2 top-1/2 ${className}`}
      style={{
        width: 'min(92vmin, 900px)',
        height: 'min(92vmin, 900px)',
        zIndex: 1,
        transform: 'translate(-50%, -50%)',
        opacity: 0.55,
        willChange: 'transform, opacity, filter',
        filter: 'drop-shadow(0 0 20px rgba(201,165,92,0.35))',
      }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 800 800"
        xmlns="http://www.w3.org/2000/svg"
        className="block w-full h-full overflow-visible"
      >
        <defs>
          <path
            id="ring-latin"
            d="M 400 400 m -340 0 a 340 340 0 1 1 680 0 a 340 340 0 1 1 -680 0"
          />
          <radialGradient id="outer-halo" cx="50%" cy="50%" r="50%">
            <stop offset="55%" stopColor="#c9a55c" stopOpacity="0" />
            <stop offset="88%" stopColor="#c9a55c" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#c9a55c" stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle cx="400" cy="400" r="390" fill="url(#outer-halo)" />

        <g fill="none" stroke="#c9a55c">
          <circle cx="400" cy="400" r="385" strokeWidth="1.4" opacity="0.65" />
          <circle cx="400" cy="400" r="380" strokeWidth="0.3" opacity="0.25" />
          <circle cx="400" cy="400" r="375" strokeWidth="0.5" opacity="0.35" />
          <circle cx="400" cy="400" r="315" strokeWidth="1" opacity="0.55" />
          <circle cx="400" cy="400" r="310" strokeWidth="0.3" opacity="0.22" />
          <circle
            cx="400"
            cy="400"
            r="305"
            strokeWidth="0.4"
            opacity="0.28"
            strokeDasharray="2 3"
          />
        </g>

        <g stroke="#c9a55c" strokeWidth="0.5" opacity="0.22">
          <g transform="translate(400 400)">
            <line x1="0" y1="-375" x2="0" y2="-315" transform="rotate(45)" />
            <line x1="0" y1="-375" x2="0" y2="-315" transform="rotate(135)" />
            <line x1="0" y1="-375" x2="0" y2="-315" transform="rotate(225)" />
            <line x1="0" y1="-375" x2="0" y2="-315" transform="rotate(315)" />
          </g>
        </g>

        <g
          style={{
            transformBox: 'view-box',
            transformOrigin: '50% 50%',
            animation: 'sigil-spin 240s linear infinite',
          }}
        >
          <text
            fontFamily="'Cinzel', serif"
            fontSize="20"
            fontWeight="500"
            fill="#e7c982"
            letterSpacing="5"
            opacity="0.8"
          >
            <textPath href="#ring-latin" startOffset="0%">
              {
                '\u2726   DEVS   OMNIPOTENS   \u00b7   CREATOR   MVNDI   \u2726   SANCTVS   DOMINVS   \u00b7   ADONAI   \u2726   HERMES   TRISMEGISTVS   \u00b7   INTELLEGO   \u2726   LVX   AETERNA   \u00b7   IGNIS   DIVINVS   '
              }
            </textPath>
          </text>
        </g>

        <g fill="#e7c982" stroke="none" opacity="0.7">
          <g transform="translate(400 400)">
            {[30, 60, 120, 150, 210, 240, 300, 330].map((deg) => (
              <g key={deg} transform={`rotate(${deg}) translate(0 -380)`}>
                <path d="M 0 -9 L 2.2 -2.2 L 9 0 L 2.2 2.2 L 0 9 L -2.2 2.2 L -9 0 L -2.2 -2.2 Z" />
              </g>
            ))}
          </g>
        </g>

        <circle
          cx="400"
          cy="400"
          r="323"
          fill="none"
          stroke="#c9a55c"
          strokeWidth="0.6"
          opacity="0.32"
          strokeDasharray="1 5"
        />

        <g transform="translate(400 400)">
          <g fill="#0e0a0f" stroke="none">
            <circle cx="0" cy="-380" r="24" />
            <circle cx="380" cy="0" r="24" />
            <circle cx="0" cy="380" r="24" />
            <circle cx="-380" cy="0" r="24" />
          </g>
          <g fill="none" stroke="#c9a55c" strokeWidth="1.2" opacity="0.7">
            <circle cx="0" cy="-380" r="24" />
            <circle cx="380" cy="0" r="24" />
            <circle cx="0" cy="380" r="24" />
            <circle cx="-380" cy="0" r="24" />
          </g>
          <g fill="none" stroke="#c9a55c" strokeWidth="0.5" opacity="0.4">
            <circle cx="0" cy="-380" r="19" />
            <circle cx="380" cy="0" r="19" />
            <circle cx="0" cy="380" r="19" />
            <circle cx="-380" cy="0" r="19" />
          </g>
        </g>

        <g
          fontFamily="'Cinzel', serif"
          fill="#ffe6aa"
          textAnchor="middle"
          dominantBaseline="central"
          opacity="0.85"
        >
          <text x="400" y="20" fontSize="28">☉</text>
          <text x="780" y="400" fontSize="28">☿</text>
          <text x="400" y="780" fontSize="28">☽</text>
          <text x="20" y="400" fontSize="28">♄</text>
        </g>
      </svg>
    </div>
  );
}
